import dbClient from './client.js';

const consoleLogs = {};

// Log levels
consoleLogs.LEVELS = {
  LOG: 'log',
  WARN: 'warn',
  ERROR: 'error',
  INFO: 'info'
};

// Sources
consoleLogs.SOURCES = {
  APP: 'app',
  SERVICE_WORKER: 'service-worker',
  COMPONENT: 'component'
};

// Add a new log entry
consoleLogs.add = async function (level, message, data = [], source = 'app', location = null) {
  try {
    const db = await dbClient.open();
    const logEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      level: level,
      message: message,
      data: data,
      source: source,
      location: location
    };

    await db.add('console_logs', logEntry);

    // Clean up old logs (keep last 2000 entries)
    await this.cleanup();

    return logEntry;
  } catch (error) {
    console.error('Error adding console log:', error);
    throw error;
  }
};

// Get logs with optional filtering
consoleLogs.get = async function (options = {}) {
  try {
    const db = await dbClient.open();
    const {
      level = null,
      source = null,
      limit = 1000,
      offset = 0,
      startDate = null,
      endDate = null
    } = options;

    let logs = [];

    if (level && source) {
      // Filter by both level and source
      const index = db.transaction('console_logs').store.index('level, timestamp');
      const range = IDBKeyRange.bound([level, startDate || ''], [level, endDate || '\uffff']);
      logs = await index.getAll(range);
      logs = logs.filter(log => log.source === source);
    } else if (level) {
      // Filter by level only
      const index = db.transaction('console_logs').store.index('level, timestamp');
      const range = IDBKeyRange.bound([level, startDate || ''], [level, endDate || '\uffff']);
      logs = await index.getAll(range);
    } else if (source) {
      // Filter by source only
      const index = db.transaction('console_logs').store.index('source, timestamp');
      const range = IDBKeyRange.bound([source, startDate || ''], [source, endDate || '\uffff']);
      logs = await index.getAll(range);
    } else {
      // Get all logs
      const index = db.transaction('console_logs').store.index('timestamp');
      const range = startDate && endDate ? IDBKeyRange.bound(startDate, endDate) : null;
      logs = await index.getAll(range);
    }

    // Sort by timestamp (oldest first) so newer logs appear at bottom
    logs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // If limit is null, return all logs (no limit)
    if (limit === null) {
      return logs.slice(offset);
    }

    return logs.slice(offset, offset + limit);
  } catch (error) {
    console.error('Error getting console logs:', error);
    throw error;
  }
};

// Get log statistics
consoleLogs.getStats = async function () {
  try {
    const db = await dbClient.open();
    const allLogs = await db.getAll('console_logs');

    const stats = {
      total: allLogs.length,
      byLevel: {},
      bySource: {},
      oldest: null,
      newest: null
    };

    allLogs.forEach(log => {
      // Count by level
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;

      // Count by source
      stats.bySource[log.source] = (stats.bySource[log.source] || 0) + 1;

      // Track timestamps
      const timestamp = new Date(log.timestamp);
      if (!stats.oldest || timestamp < new Date(stats.oldest)) {
        stats.oldest = log.timestamp;
      }
      if (!stats.newest || timestamp > new Date(stats.newest)) {
        stats.newest = log.timestamp;
      }
    });

    return stats;
  } catch (error) {
    console.error('Error getting console log stats:', error);
    throw error;
  }
};

// Clean up old logs (keep last 2000 entries)
consoleLogs.cleanup = async function () {
  try {
    const db = await dbClient.open();
    const allLogs = await db.getAll('console_logs');

    if (allLogs.length > 2000) {
      // Sort by timestamp (oldest first)
      allLogs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Delete oldest logs beyond the limit
      const logsToDelete = allLogs.slice(0, allLogs.length - 2000);

      const transaction = db.transaction('console_logs', 'readwrite');
      const store = transaction.objectStore('console_logs');

      for (const log of logsToDelete) {
        await store.delete(log.id);
      }

      // Only log if we removed more than 10 logs to reduce noise
      if (logsToDelete.length > 10) {
        console.log(`Cleaned up ${logsToDelete.length} old console logs`);
      }
    }
  } catch (error) {
    console.error('Error cleaning up console logs:', error);
  }
};

// Clear all logs
consoleLogs.clear = async function () {
  try {
    const db = await dbClient.open();
    const transaction = db.transaction('console_logs', 'readwrite');
    const store = transaction.objectStore('console_logs');
    await store.clear();
    console.log('All console logs cleared');
  } catch (error) {
    console.error('Error clearing console logs:', error);
    throw error;
  }
};

// Delete logs older than specified days
consoleLogs.deleteOlderThan = async function (days) {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const db = await dbClient.open();
    const index = db.transaction('console_logs').store.index('timestamp');
    const range = IDBKeyRange.upperBound(cutoffDate.toISOString());
    const oldLogs = await index.getAll(range);

    const transaction = db.transaction('console_logs', 'readwrite');
    const store = transaction.objectStore('console_logs');

    for (const log of oldLogs) {
      await store.delete(log.id);
    }

    console.log(`Deleted ${oldLogs.length} console logs older than ${days} days`);
    return oldLogs.length;
  } catch (error) {
    console.error('Error deleting old console logs:', error);
    throw error;
  }
};

export default consoleLogs; 